import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// =============================================================================
// NATIVE SPLASH — held on BOTH platforms until the app is ready (no early hide)
// -----------------------------------------------------------------------------
// Both platforms now have a BRANDED native splash that shows the penguin logo:
//   • iOS     — the native launch storyboard (generated by @capacitor/assets).
//   • Android — the Android-12 SplashScreen `windowSplashScreenAnimatedIcon`
//               (`@drawable/splash_icon`, penguin on white) configured in
//               `android-config/styles_v31.xml` → res/values-v31/styles.xml.
//
// So we HOLD the native splash over the entire cold-start and lift it only at
// `app-ready` (see `dismissSplash`). We do NOT hide it early. Hiding it early
// exposes the HTML `#splash` underneath — on iOS its flex-centered logo dipped
// ~166px the instant `useNativeShellInit` resized the WebView viewport (status-
// bar overlay + safe-area settling; on-device frame 79), and on Android it
// showed a brief HTML-splash flash (logo + tagline) after the native penguin.
// Holding the stable native splash hides all that WebView settling behind it —
// the single clean native-splash model used by Zomato et al.
// =============================================================================

// =============================================================================
// IMMEDIATE NATIVE PERMISSION REQUESTS
// -----------------------------------------------------------------------------
// Fire location + notification permission prompts the moment the JS bundle
// loads — BEFORE React renders, BEFORE any component-level useEffect runs.
// The OS prompt shows within ~100ms of app launch.
//
// This runs in parallel with React boot (fire-and-forget) so it doesn't slow
// the splash. Both `Geolocation.requestPermissions` and
// `LocalNotifications.requestPermissions` are idempotent — calling them when
// permissions are already granted is a free no-op. Re-checked again later
// from `useNativeShellInit` as a safety net for edge cases (script-loading
// race, plugin not yet registered, etc.).
// =============================================================================
(async function requestNativePermissionsEarly() {
  // Only run inside the Capacitor native shell (not in PWA/web).
  if (!window?.Capacitor?.isNativePlatform?.()) return;

  // Location — Android needs both fine + coarse for the geolocation plugin
  // to settle on a single OS-level dialog.
  try {
    const { Geolocation } = await import("@capacitor/geolocation");
    const cur = await Geolocation.checkPermissions();
    console.info("[boot-perm] location current →", cur);
    if (cur.location !== "granted" && cur.coarseLocation !== "granted") {
      const req = await Geolocation.requestPermissions({
        permissions: ["location", "coarseLocation"],
      });
      console.info("[boot-perm] location requested →", req);
      // Mirror useNativeShellInit: if the user just granted permission, let
      // Landing.js know so it can run auto-detection without waiting for the
      // hook's redundant re-request to fire the same event.
      if (req.location === "granted" || req.coarseLocation === "granted") {
        sessionStorage.removeItem("branchAutoDetected");
        window.dispatchEvent(new CustomEvent("pb-location-granted"));
      }
    }
  } catch (e) {
    console.warn("[boot-perm] location request failed", e?.message);
  }

  // Notifications (Android 13+ POST_NOTIFICATIONS, iOS UNUserNotificationCenter).
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const cur = await LocalNotifications.checkPermissions();
    console.info("[boot-perm] notifications current →", cur);
    if (cur.display !== "granted" && cur.display !== "denied") {
      const req = await LocalNotifications.requestPermissions();
      console.info("[boot-perm] notifications requested →", req);
    }
  } catch (e) {
    console.warn("[boot-perm] notifications request failed", e?.message);
  }
})();

// =============================================================================
// SPLASH SCREEN DISMISSAL
// -----------------------------------------------------------------------------
// Splash dismisses on the `app-ready` event which the active page dispatches
// AFTER its data has loaded (Landing.js fires it once branches + menu are
// populated; App.js fires it instantly for any non-Landing route since they
// have their own loading skeletons). Keeps splash visible across the full
// cold-start delay so customers never see a flash of empty content between
// splash and real UI. 5s safety net so a backend hiccup never traps users.
//
// Cold-start WebView repaint guard (Android). While the HTML splash is on
// screen (`#root` sitting at opacity:0 underneath), Android's WebView GPU
// compositor can mark the underlying surface as inactive — when the splash
// fades out and `#root` cross-fades in, the framebuffer stays stale and the
// customer sees a white screen until a touch event invalidates the view.
// This is the same class of bug that the `appStateChange` handler in
// `useNativeShellInit.js` fixes on RESUME (commit fe6e335). The resume fix
// does not cover cold start because `appStateChange` only fires on
// foreground transitions. We mirror the trick here on the cold-start path:
// after the splash is removed, toggle a one-frame transform on `<body>` to
// force the WebView to mark the layer dirty and request a fresh frame.
// Harmless on the PWA / web build — the browser handles repaints fine.
// =============================================================================
// One-frame WebView repaint nudge — forces the Android WebView's GPU compositor
// to flush a fresh frame (see the cold-start note above). No-op-ish on iOS.
function forceRepaint() {
  if (!document.body) return;
  document.body.style.transform = "translateZ(0)";
  requestAnimationFrame(() => {
    if (document.body) document.body.style.transform = "";
  });
}

let splashDismissed = false;
async function dismissSplash() {
  if (splashDismissed) return;
  splashDismissed = true;

  const splash = document.getElementById("splash");
  const rootEl = document.getElementById("root");
  const isNative = !!window?.Capacitor?.isNativePlatform?.();

  // Reveal the React app sitting underneath the splash.
  if (rootEl) rootEl.classList.add("ready");

  if (isNative) {
    // NATIVE (iOS + Android): the branded native splash (penguin) has covered the
    // entire cold-start, so the HTML #splash sat behind it, never seen. The app
    // is now ready and fully settled — remove the hidden HTML #splash instantly,
    // then lift the native splash with a gentle fade to reveal the real screen.
    if (splash) splash.remove();
    forceRepaint();
    try {
      const { SplashScreen } = await import("@capacitor/splash-screen");
      await SplashScreen.hide({ fadeOutDuration: 200 });
    } catch (e) {
      console.warn("[boot] native splash hide failed", e?.message);
    }
    return;
  }

  // WEB / PWA: no native splash — the HTML #splash is the only splash. Fade it out.
  if (!splash) return;
  splash.style.transition = "opacity .25s ease-out";
  splash.style.opacity = "0";
  setTimeout(() => {
    splash.remove();
    forceRepaint();
  }, 260);
}

window.addEventListener("app-ready", dismissSplash, { once: true });
// Safety net: never trap the user behind the splash if `app-ready` never fires.
setTimeout(dismissSplash, 5000);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
